/**
 * Unit-neutral prose. Generated sentences ("High 21°, low 14°. Gusts to 30 mph.") are written
 * once with their values as SI tokens — `{t:21.1}` — and rendered in the reader's units by
 * `fillText` at the moment they are shown. A report, a cache entry or an edge response therefore
 * never locks in °F or mph: change the units and the same stored text reads in the new ones.
 *
 * Tokens: `t` temperature °C · `w` wind m/s · `w5` wind m/s rounded to 5 (sentences) · `c` clock
 * `epochMs|zone` · `v` visibility m · `a` height above ground m · `p` precipitation mm · `s` snow cm
 * · `h` pressure hPa · `d` distance km (to a station or monitor). Unknown tokens and text without tokens pass through untouched.
 */

import { fastClock } from './tzfast'
import { tok } from './tok'
import { fmtPrecip, fmtPressure, fmtSnow, fmtTemp, fmtWind, METRIC_UNITS, US_UNITS, windLabel, wind, type Units } from './units'

export { tok }

const TOKEN = /\{(t|w5|w|c|v|a|p|s|h|d):([^{}|]*)(?:\|([^{}]*))?\}/g

/** "5 PM" (12-hour, hour only) or "17:00" (24-hour). */
export function clockText(ms: number, timeZone: string, clock: 12 | 24): string {
  const fast = fastClock(ms, timeZone, clock, clock === 24)
  if (fast != null) return fast
  const f = new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', ...(clock === 24 ? { minute: '2-digit', hourCycle: 'h23' as const } : {}) })
  return f.format(new Date(ms))
}

/** Renders every token in `text` in `units`; text without tokens is returned as is. */
export function fillText(text: string, units: Units): string
export function fillText(text: string | null | undefined, units: Units): string | null | undefined
export function fillText(text: string | null | undefined, units: Units): string | null | undefined {
  if (!text || !text.includes('{')) return text
  return text.replace(TOKEN, (match, kind: string, value: string, zone?: string) => {
    const x = Number(value)
    if (!Number.isFinite(x)) return match
    switch (kind) {
      case 't':
        return fmtTemp(x, units.temp)
      case 'w':
        return fmtWind(x, units.wind)
      case 'w5': {
        // Sentences round to 5 mph / km/h / kn; m/s is already coarse, so it rounds to 1.
        const step = units.wind === 'ms' ? 1 : 5
        return `${Math.round(wind(x, units.wind)! / step) * step} ${windLabel(units.wind)}`
      }
      case 'c':
        return zone ? clockText(x, zone, units.clock) : match
      case 'v':
        return units.distance === 'mi' ? `${Math.round(x / 1609.34)} mi` : `${Math.round(x / 1000)} km`
      case 'a':
        return units.distance === 'mi'
          ? `${(Math.round((x * 3.28084) / 100) * 100).toLocaleString('en-US')} ft`
          : `${(Math.round(x / 10) * 10).toLocaleString('en-US')} m`
      case 'p':
        return fmtPrecip(x, units.precip)
      case 's':
        return fmtSnow(x, units.precip)
      case 'h':
        return fmtPressure(x, units.pressure)
      case 'd':
        return units.distance === 'mi' ? `${Math.round(x * 0.621371)} mi` : `${Math.round(x)} km`
    }
    return match
  })
}

/** The two systems the API speaks (`units=us|metric`) as full unit sets (metric reads a 24-hour clock). */
export const unitsOf = (system: 'us' | 'metric'): Units => (system === 'metric' ? METRIC_UNITS : US_UNITS)

/** True when `text` still carries tokens (a path nobody filled). */
export const hasTokens = (text: string): boolean => new RegExp(TOKEN.source).test(text)

type Mapper = (text: string) => string

const mapAll = (list: string[], f: Mapper): string[] => {
  let out: string[] | null = null
  list.forEach((s, i) => {
    const t = f(s)
    if (t !== s) (out ??= list.slice())[i] = t
  })
  return out ?? list
}

/**
 * The report with every generated sentence rendered in `units`. Sections without tokens keep their
 * identity, so a render that only changed units re-renders only what carries text.
 */
export function fillReport<R extends FillableReport>(report: R, units: Units): R {
  const f: Mapper = (s) => fillText(s, units)
  const summaries = {
    hourly: f(report.summaries.hourly),
    today: f(report.summaries.today),
    week: f(report.summaries.week),
  }
  const sameSummaries = summaries.hourly === report.summaries.hourly && summaries.today === report.summaries.today && summaries.week === report.summaries.week
  let dailyChanged = false
  const daily = report.daily.map((d) => {
    const summary = f(d.summary)
    if (summary === d.summary) return d
    dailyChanged = true
    return { ...d, summary }
  })
  const method = f(report.minutely.method)
  const note = f(report.current.provenance.note)
  const fusionMethod = f(report.fusion.method)
  const notes = mapAll(report.fusion.notes, f)
  let sourcesChanged = false
  const sources = report.sources.map((s) => {
    const detail = f(s.detail)
    if (detail === s.detail) return s
    sourcesChanged = true
    return { ...s, detail }
  })
  const aqSource = report.air_quality ? f(report.air_quality.source) : null
  const aqSame = !report.air_quality || aqSource === report.air_quality.source
  if (aqSame && sameSummaries && !dailyChanged && note === report.current.provenance.note && method === report.minutely.method && fusionMethod === report.fusion.method && notes === report.fusion.notes && !sourcesChanged) return report
  return {
    ...report,
    current: note === report.current.provenance.note ? report.current : { ...report.current, provenance: { ...report.current.provenance, note } },
    summaries: sameSummaries ? report.summaries : summaries,
    daily: dailyChanged ? daily : report.daily,
    minutely: method === report.minutely.method ? report.minutely : { ...report.minutely, method },
    fusion: fusionMethod === report.fusion.method && notes === report.fusion.notes ? report.fusion : { ...report.fusion, method: fusionMethod, notes },
    sources: sourcesChanged ? sources : report.sources,
    ...(aqSame || !report.air_quality ? {} : { air_quality: { ...report.air_quality, source: aqSource as string } }),
  }
}

/** The parts of a WeatherReport that carry generated sentences. */
export interface FillableReport {
  summaries: { hourly: string; today: string; week: string }
  daily: { summary: string }[]
  current: { provenance: { note: string } }
  minutely: { method: string }
  fusion: { method: string; notes: string[] }
  sources: { detail: string }[]
  air_quality?: { source: string } | null
}
